import DataList from './DataList';
import React, { useState, useEffect } from 'react';
import { PriceCategory } from '../types/priceCategory';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import EditIcon from '@mui/icons-material/Edit';
import { apiClient } from '../contexts/AuthContext'; // Import apiClient

interface EditDialogProps {
  open: boolean;
  category: PriceCategory | null;
  onClose: () => void;
  onSave: (hourlyRate: number, nightlyRate: number) => void;
}

const EditDialog: React.FC<EditDialogProps> = ({ open, category, onClose, onSave }) => {
  const [hourlyRate, setHourlyRate] = useState<string>('');
  const [nightlyRate, setNightlyRate] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (category) {
      setHourlyRate(category.default_hourly_rate.toString());
      setNightlyRate(category.default_nightly_rate.toString());
    }
  }, [category]);

  const handleSave = () => {
    const hourly = parseFloat(hourlyRate);
    const nightly = parseFloat(nightlyRate);

    if (isNaN(hourly) || hourly <= 0) {
      setError('Veuillez entrer un taux horaire valide supérieur à 0');
      return;
    }

    if (isNaN(nightly) || nightly <= 0) {
      setError('Veuillez entrer un taux de nuit valide supérieur à 0');
      return;
    }

    onSave(hourly, nightly);
  };

  if (!category) return null;

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        Modifier les tarifs pour {category.name}
      </DialogTitle>
      <DialogContent>
        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}
        <TextField
          label="Taux horaire par défaut (€)"
          type="number"
          value={hourlyRate}
          onChange={(e) => setHourlyRate(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, step: 0.01 }}
        />
        <TextField
          label="Taux de nuit par défaut (€)"
          type="number"
          value={nightlyRate}
          onChange={(e) => setNightlyRate(e.target.value)}
          fullWidth
          margin="normal"
          inputProps={{ min: 0, step: 0.01 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Annuler</Button>
        <Button onClick={handleSave} variant="contained" color="primary">
          Enregistrer les modifications
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Styled TableRow for alternating colors (copied from AuxiliaireManagement)
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const PriceCategoryManagement: React.FC = () => {
  const [priceCategories, setPriceCategories] = useState<PriceCategory[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [editingCategory, setEditingCategory] = useState<PriceCategory | null>(null);

  const fetchPriceCategories = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get<PriceCategory[]>('/api/price-categories');
      const formattedData = response.data.map((category: any) => ({
        ...category,
        default_hourly_rate: parseFloat(category.default_hourly_rate),
        default_nightly_rate: parseFloat(category.default_nightly_rate),
      }));
      setPriceCategories(formattedData);
    } catch (e: any) {
      setError(`Échec de la récupération des catégories de prix : ${e.message || 'Unknown error'}`);
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPriceCategories();
  }, []);

  const handleEdit = (category: PriceCategory) => {
    setEditingCategory(category);
  };

  const handleSave = async (hourlyRate: number, nightlyRate: number) => {
    if (!editingCategory) return;
    setError(null);
    try {
      const response = await apiClient.put<PriceCategory>(`/api/price-categories/${editingCategory.id}`, {
        name: editingCategory.name,
        category: editingCategory.category,
        default_hourly_rate: hourlyRate,
        default_nightly_rate: nightlyRate,
      });
      
      setPriceCategories(prev => 
        prev.map(cat => cat.id === editingCategory.id ? response.data : cat)
      );
      setEditingCategory(null);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const message = e?.message;
      setError(`Échec de la mise à jour de la catégorie de prix: ${detail || message || 'Unknown error'}`);
      console.error(e);
    }
  };

  if (loading) {
    // Consistent loading indicator
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    // Use Box with responsive padding
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Poppins, Inter, sans-serif', fontWeight: 700 }}>
        Gestion des catégories de prix
      </Typography>

      {/* Consistent error display */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* TODO: Add "Create New" Button/Form here - maybe using Paper and Grid like AuxiliaireManagement? */}

      {/* Use MUI Table components */}
      <DataList
        rows={priceCategories}
        keyOf={(c) => c.id}
        empty="Aucune catégorie de prix trouvée."
        fields={[
          { label: 'Nom', primary: true, render: (c) => c.name },
          { label: "Catégorie d'auxiliaire", render: (c) => c.category },
          { label: 'Taux horaire (€)', render: (c) => c.default_hourly_rate.toFixed(2) },
          { label: 'Taux de nuit (€)', render: (c) => c.default_nightly_rate.toFixed(2) },
        ]}
        actions={(c) => (
          <Tooltip title="Modifier les tarifs">
            <IconButton onClick={() => handleEdit(c)} color="primary" size="small">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        table={(
<TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="price category table">
          <TableHead>
            <TableRow>
              {/* Bold headers */}
              <TableCell sx={{ fontWeight: 'bold' }}>Nom</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Catégorie d'auxiliaire</TableCell>
              <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Taux horaire par défaut (€)</TableCell>
              <TableCell sx={{ minWidth: 150, fontWeight: 'bold' }}>Taux de nuit par défaut (€)</TableCell>
              <TableCell align="right" sx={{ minWidth: 100, fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {priceCategories.length > 0 ? (
              priceCategories.map((category) => (
                // Use StyledTableRow
                <StyledTableRow key={category.id}>
                  <TableCell component="th" scope="row">{category.name}</TableCell>
                  <TableCell>{category.category}</TableCell>
                  <TableCell>{category.default_hourly_rate.toFixed(2)}</TableCell>
                  <TableCell>{category.default_nightly_rate.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Modifier les tarifs"> 
                      <Button
                        onClick={() => handleEdit(category)}
                        // variant="outlined"
                        color="primary" // Use primary color for edit
                        size="small"
                        sx={{ p: 0.5, minWidth: 'auto' }} // Consistent button styling
                      >
                        <EditIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  </TableCell>
                </StyledTableRow>
              ))
            ) : (
              // Consistent empty state
              !loading && (
                <StyledTableRow>
                  <TableCell colSpan={5} align="center">
                    Aucune catégorie de prix trouvée.
                  </TableCell>
                </StyledTableRow>
              )
            )}
          </TableBody>
        </Table>
      </TableContainer>
        )}
      />

      <EditDialog
        open={editingCategory !== null}
        category={editingCategory}
        onClose={() => setEditingCategory(null)}
        onSave={handleSave}
      />
    </Box>
  );
};

export default PriceCategoryManagement; 